import type {
  MaterialTranslateAlgoZhParams,
  MaterialTranslateAlgoZhStatusResult,
} from './translate-algo-zh'
import type {
  VideoShotsListParams,
  VideoShotsListResponse,
} from './video-shots'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import { MATERIAL_TEXT_REWRITE_STATUS } from '#/views/material-analysis/material-library/constants'

import { normalizeMaterialTranslateAlgoZhStatusResponse } from './translate-algo-zh'
import { normalizeVideoShotsListResponse } from './video-shots'

export interface MaterialsListParams {
  pageIndex?: number
  pageSize?: number
  startDate?: string
  endDate?: string
  firstCategory?: string
  mediaPlatforms?: string[]
  source?: 'platform_sync' | 'user_upload'
  materialId?: number | string
  materialIds?: Array<number | string>
  materials?: MaterialSrRef[]
  status?: string
  algoAnalysisStatuses?: Array<'failure' | 'in_process' | 'pending' | 'success'>
  orderByActiveAdsCount?: 'asc' | 'desc'
  orderByTotalAdsCount?: 'asc' | 'desc'
  orderBySpend?: 'asc' | 'desc'
  orderByRoas?: 'asc' | 'desc'
  orderByLctr?: 'asc' | 'desc'
  orderByLcvr?: 'asc' | 'desc'
  totalAdsCountMin?: number
  totalAdsCountMax?: number
  activeAdsCountMin?: number
  activeAdsCountMax?: number
  spendMin?: number
  spendMax?: number
  roasMin?: number
  roasMax?: number
  lctrMin?: number
  lctrMax?: number
  lcvrMin?: number
  lcvrMax?: number
}

export interface MaterialsListItem {
  id: number | string
  materialId?: string
  mediaPlatform: string
  name: string
  md5: string
  type: string
  source: string
  storagePath: string
  storageType: string
  fileSize: number
  firstCategory: string
  storeDomain?: string
  totalAdsCount: number
  activeAdsCount: number
  spend: string
  roas: string
  linkClicks?: null | number | string
  lcpc?: null | number | string
  lctr?: null | number | string
  lcvr?: null | number | string
  ctr?: string
  cvr?: string
  reportDate: string
  storageUrl: string
  createdTime: string
  status?: string
  algoAnalysisStatus?: 'failure' | 'in_process' | 'pending' | 'success'
}

export interface MaterialDetailItem {
  id?: string
  materialId?: string
  mediaPlatform?: string
  name?: string
  md5?: string
  type?: string
  source?: string
  storagePath?: string
  storageType?: string
  fileSize?: number
  firstCategory?: string
  storeDomain?: string
  totalAdsCount?: number
  activeAdsCount?: number
  spend?: number | string
  roas?: number | string
  impressions?: number | string
  clicks?: number | string
  linkClicks?: null | number | string
  conversions?: number | string
  conversionValue?: number | string
  cpa?: number | string
  cpc?: number | string
  lcpc?: null | number | string
  cpm?: number | string
  ctr?: number | string
  lctr?: null | number | string
  cvr?: number | string
  lcvr?: null | number | string
  videoPlayActions?: number | string
  videoThruplayWatchedActions?: number | string
  videoP25WatchedActions?: number | string
  videoP50WatchedActions?: number | string
  videoP75WatchedActions?: number | string
  videoP95WatchedActions?: number | string
  videoP100WatchedActions?: number | string
  launchTime?: string
  reportDate?: string
  storageUrl?: string
  createdTime?: string
  status?: string
  algoAnalysisStatus?: 'failure' | 'in_process' | 'pending' | 'success'
  algoCompleteTime?: string
  algoAnalysisResult?: null | Record<string, unknown>
  algoAnalysisZhResult?: null | Record<string, unknown>
  algoAnalysisZhStatus?: MaterialTranslateAlgoZhStatusResult['algoAnalysisZhStatus']
  algoRewriteResult?: MaterialTextRewriteRawResult[] | null
  aggrList?: MaterialDetailAggrItem[] | null
}

export interface MaterialDetailAggrItem {
  reportDate?: string
  totalAdsCount?: number | string
  activeAdsCount?: number | string
  spend?: number | string
  roas?: number | string
  impressions?: number | string
  clicks?: number | string
  linkClicks?: null | number | string
  conversions?: number | string
  conversionValue?: number | string
  cpa?: number | string
  cpc?: number | string
  lcpc?: null | number | string
  cpm?: number | string
  ctr?: number | string
  lctr?: null | number | string
  cvr?: number | string
  lcvr?: null | number | string
}

export interface MaterialDetailParams {
  endDate?: string
  materialId: string
  mediaPlatform: string
  startDate?: string
}

interface MaterialsListResponse {
  data?: {
    list?: MaterialsListItem[]
    pageIndex?: number
    pageSize?: number
    total?: number
  }
  list?: MaterialsListItem[]
  pageIndex?: number
  pageSize?: number
  total?: number
}

interface MaterialDetailResponse {
  data?: MaterialDetailItem
}

export interface SubmitAnalysisParams {
  materials: MaterialSrRef[]
}

export interface MaterialTextRewriteParams {
  materialId: string
  mediaPlatform: string
  prompt?: string
}

export interface MaterialTextRewriteRawResult {
  code?: string
  costtime?: number
  data?: string
  message?: string
  request_id?: string
  success?: boolean
  uuid?: string
}

interface MaterialTextRewriteStatusData {
  algoRewriteRequestId?: string
  algoRewriteResult?: MaterialTextRewriteRawResult[] | null
  algoRewriteStatus?: MaterialTextRewriteStatusValue
}

interface MaterialTextRewriteStatusResponse {
  code?: number
  cost?: string
  data?: MaterialTextRewriteStatusData | null
  msg?: string
  traceID?: string
}

export interface MaterialTextRewriteStatusResult {
  algoRewriteRequestId: string
  algoRewriteResult: MaterialTextRewriteRawResult[] | null
  algoRewriteStatus: '' | MaterialTextRewriteStatusValue
  msg: string
}

type MaterialTextRewriteStatusValue =
  (typeof MATERIAL_TEXT_REWRITE_STATUS)[keyof typeof MATERIAL_TEXT_REWRITE_STATUS]

interface PollAlgoStatusData {
  failureMaterials?: MaterialSrRef[]
  successMaterials?: MaterialSrRef[]
}

interface PollAlgoStatusResponse {
  data?: PollAlgoStatusData | PollAlgoStatusData[]
}

export interface PollAlgoStatusResult {
  failureMaterials: MaterialSrRef[]
  successMaterials: MaterialSrRef[]
}

/**
 * SR 新版素材的联合主键。
 * 前端展示层仍可把 materialId 映射成内部 id，但所有会落到后端的操作都必须带上媒体平台，
 * 否则同一个 materialId 在不同平台下可能无法唯一定位素材。
 */
export interface MaterialSrRef {
  materialId: string
  mediaPlatform: string
}

const normalizeMaterialsListResponse = (
  response: MaterialsListItem[] | MaterialsListResponse,
  params: MaterialsListParams,
) => {
  const normalizeItem = (item: MaterialsListItem) => {
    const materialId = String(item.materialId || item.id || '').trim()
    return {
      ...item,
      id: materialId,
      materialId,
    }
  }

  if (Array.isArray(response)) {
    const items = response.map((item) => normalizeItem(item))
    return {
      items,
      pageIndex: Number(params.pageIndex || 1),
      pageSize: Number(params.pageSize || 20),
      total: items.length,
    }
  }

  const list =
    response?.list || response?.data?.list || ([] as MaterialsListItem[])
  const items = list.map((item) => normalizeItem(item))

  return {
    items,
    pageIndex: Number(
      response?.pageIndex || response?.data?.pageIndex || params.pageIndex || 1,
    ),
    pageSize: Number(
      response?.pageSize || response?.data?.pageSize || params.pageSize || 20,
    ),
    total: Number(
      response?.total || response?.data?.total || Math.max(items.length, 0),
    ),
  }
}

const normalizePollAlgoStatusResponse = (
  response: PollAlgoStatusData | PollAlgoStatusData[] | PollAlgoStatusResponse,
): PollAlgoStatusResult => {
  const toMaterialList = (materials: unknown) => {
    if (!Array.isArray(materials)) return [] as MaterialSrRef[]
    return materials
      .map((item) => {
        const payload =
          item && typeof item === 'object'
            ? (item as Partial<MaterialSrRef>)
            : {}
        return {
          materialId: String(payload.materialId || '').trim(),
          mediaPlatform: String(payload.mediaPlatform || '').trim(),
        }
      })
      .filter((item) => item.materialId && item.mediaPlatform)
  }

  if (Array.isArray(response)) {
    const first = response[0]
    return {
      successMaterials: toMaterialList(first?.successMaterials),
      failureMaterials: toMaterialList(first?.failureMaterials),
    }
  }

  const data =
    response &&
    typeof response === 'object' &&
    'data' in response &&
    response.data
      ? response.data
      : response

  if (Array.isArray(data)) {
    const first = data[0]
    return {
      successMaterials: toMaterialList(first?.successMaterials),
      failureMaterials: toMaterialList(first?.failureMaterials),
    }
  }

  const payload =
    data && typeof data === 'object' ? (data as PollAlgoStatusData) : undefined

  return {
    successMaterials: toMaterialList(payload?.successMaterials),
    failureMaterials: toMaterialList(payload?.failureMaterials),
  }
}

const normalizeMaterialTextRewriteStatusResponse = (
  response: MaterialTextRewriteStatusResponse,
): MaterialTextRewriteStatusResult => {
  const data = response?.data
  return {
    algoRewriteRequestId: data?.algoRewriteRequestId?.trim?.() || '',
    algoRewriteResult: Array.isArray(data?.algoRewriteResult)
      ? data.algoRewriteResult
      : null,
    algoRewriteStatus: data?.algoRewriteStatus || '',
    msg: response?.msg?.trim?.() || '',
  }
}

export const materialAnalysisMaterialsApi = defineApi(
  'operation',
  ({ prefix }) => {
    function getMaterialsList(params: MaterialsListParams) {
      return requestClient
        .post<
          MaterialsListItem[] | MaterialsListResponse
        >(prefix('materials/list-sr'), params)
        .then((res) => normalizeMaterialsListResponse(res, params))
    }

    function getMaterialDetail(params: MaterialDetailParams) {
      return requestClient
        .post<
          MaterialDetailItem | MaterialDetailResponse
        >(prefix('materials/detail-sr'), params)
        .then((res) => {
          const detail =
            res && typeof res === 'object' && 'data' in res
              ? (res as MaterialDetailResponse).data ||
                ({} as MaterialDetailItem)
              : ((res || {}) as MaterialDetailItem)
          const materialId = String(
            detail.materialId || detail.id || params.materialId,
          ).trim()
          return {
            ...detail,
            id: materialId,
            materialId,
            mediaPlatform: detail.mediaPlatform || params.mediaPlatform,
          }
        })
    }

    function submitAnalysis(params: SubmitAnalysisParams) {
      return requestClient.post(prefix('materials/submit-analysis-sr'), params)
    }

    function pollAlgoStatus(params: SubmitAnalysisParams) {
      return requestClient
        .post<
          PollAlgoStatusData | PollAlgoStatusData[] | PollAlgoStatusResponse
        >(prefix('materials/poll-algo-status-sr'), params)
        .then((res) => normalizePollAlgoStatusResponse(res))
    }

    function submitTextRewrite(params: MaterialTextRewriteParams) {
      return requestClient.post(
        prefix('materials/submit-text-rewrite-sr'),
        params,
      )
    }

    function pollTextRewriteStatus(params: MaterialTextRewriteParams) {
      return requestClient
        .post<MaterialTextRewriteStatusResponse>(
          prefix('materials/text-rewrite-status-sr'),
          params,
          {
            responseReturn: 'raw',
          },
        )
        .then((res) =>
          normalizeMaterialTextRewriteStatusResponse(
            res?.data as MaterialTextRewriteStatusResponse,
          ),
        )
    }

    function submitTranslateAlgoZh(params: MaterialTranslateAlgoZhParams) {
      return requestClient.post(
        prefix('materials/submit-translate-algo-zh-sr'),
        params,
      )
    }

    function pollTranslateAlgoZhStatus(
      params: MaterialTranslateAlgoZhParams,
    ): Promise<MaterialTranslateAlgoZhStatusResult> {
      return requestClient
        .post(prefix('materials/translate-algo-zh-status-sr'), params, {
          responseReturn: 'raw',
        })
        .then((res) =>
          normalizeMaterialTranslateAlgoZhStatusResponse(
            res?.data as Parameters<
              typeof normalizeMaterialTranslateAlgoZhStatusResponse
            >[0],
          ),
        )
    }

    function getVideoShotsList(params: VideoShotsListParams) {
      return requestClient
        .get<VideoShotsListResponse>(prefix('materials/video-shots-sr'), {
          params,
        })
        .then((res) => normalizeVideoShotsListResponse(res, params))
    }

    return {
      getMaterialDetail,
      getMaterialsList,
      getVideoShotsList,
      pollAlgoStatus,
      pollTextRewriteStatus,
      pollTranslateAlgoZhStatus,
      submitAnalysis,
      submitTextRewrite,
      submitTranslateAlgoZh,
    }
  },
)
